/**
 * A travel day, read off its legs.
 *
 * A travel segment (`segmentType: "travel"`) holds the day's legs as ordinary
 * activities, side by side in the order they happened: ride share → airport →
 * flight → airport → ride share. Nothing about the chain is stored; the
 * summary on the segment is worked out from the legs each time, so editing a
 * leg can never leave it stale.
 */

import type { EntityType, InteractionEvent } from "../types";
import { airportShortName, flightRoute } from "./flightRoute";
import { getActivityTimeRange, isFlightName } from "./tripDisplay";

export interface TravelLegLike {
  entityId?: string | null;
  name?: string | null;
  customResponses?: Record<string, unknown> | null;
  events?: InteractionEvent[];
}

export interface TravelPlaceInfo {
  displayName: string;
  type: EntityType | null;
}

export interface TravelDaySummary {
  /** "BOS → MCO", or null when no leg says where it went. */
  route: string | null;
  /** First logged step to last, in ms; null with fewer than two times. */
  doorToDoorMs: number | null;
  /** How many flights the day had. */
  flights: number;
}

function isFlightLeg(leg: TravelLegLike, place: TravelPlaceInfo | undefined): boolean {
  const route = flightRoute(leg.customResponses ?? undefined);
  if (route.from || route.to) return true;
  if (place?.type === "TRANSPORTATION" && isFlightName(place.displayName)) return true;
  return !place && isFlightName(leg.name);
}

/**
 * The day's route, door-to-door time and flight count.
 *
 * The route runs from the first flight's departure airport to the last
 * flight's arrival airport, so a connection in between drops out of it. With
 * no airports on the flights it falls back to the first and last Airport
 * places among the legs, which is what a day logged before flights carried
 * their airports has.
 */
export function travelDaySummary(
  legs: TravelLegLike[],
  placeOf: (id: string) => TravelPlaceInfo | undefined,
): TravelDaySummary {
  const timed = legs
    .map((leg) => ({ leg, range: getActivityTimeRange(leg.events) }))
    .filter((x) => x.range.start != null)
    .sort((a, b) => a.range.start!.getTime() - b.range.start!.getTime());

  let first = Infinity;
  let last = -Infinity;
  for (const { range } of timed) {
    first = Math.min(first, range.start!.getTime());
    last = Math.max(last, (range.end ?? range.start)!.getTime());
  }
  const doorToDoorMs = last > first ? last - first : null;

  const ordered = [...timed.map((x) => x.leg), ...legs.filter((l) => getActivityTimeRange(l.events).start == null)];
  const place = (leg: TravelLegLike) => (leg.entityId ? placeOf(leg.entityId) : undefined);
  const flights = ordered.filter((leg) => isFlightLeg(leg, place(leg)));

  const code = (id: string | null) => {
    if (!id) return null;
    return airportShortName(placeOf(id)?.displayName);
  };
  let from = null as string | null;
  let to = null as string | null;
  for (const f of flights) {
    from ??= code(flightRoute(f.customResponses ?? undefined).from);
  }
  for (const f of [...flights].reverse()) {
    to ??= code(flightRoute(f.customResponses ?? undefined).to);
  }
  if (!from && !to) {
    const airports = ordered.filter((leg) => place(leg)?.type === "AIRPORT");
    if (airports.length >= 2) {
      from = airportShortName(place(airports[0]!)?.displayName);
      to = airportShortName(place(airports[airports.length - 1]!)?.displayName);
    }
  }
  const route = from || to ? `${from ?? "?"} → ${to ?? "?"}` : null;

  return { route, doorToDoorMs, flights: flights.length };
}
